import { useMemo, useState } from 'react'

import { EmptyState, ErrorState, SkeletonRows } from '@/shared/components/data'
import { KanbanBoard, type KanbanColumnSpec } from '@/shared/components/dnd'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import type { Stage, Task } from '@/shared/types/domain'

import { useCompleteTask, useDeleteTask, useProjectTasks, useUpdateTask } from '../api'
import { useMoveTask } from '../reorder'

import { TaskCard } from './TaskCard'
import { TaskDetailSheet } from './TaskDetailSheet'

export interface TaskBoardProps {
  projectId: string
  stages: readonly Stage[]
  className?: string
}

/**
 * El tablero de tareas del proyecto.
 *
 * Comparte query, rank y mutaciones con `TaskList`: cambiar de vista no cambia
 * de fuente de datos, solo de forma de agrupar. Por eso mover una tarjeta acá
 * mueve la fila allá, sin ninguna sincronización explícita.
 */
export function TaskBoard({ projectId, stages, className }: TaskBoardProps) {
  const t = useT()
  const query = useProjectTasks(projectId)
  const move = useMoveTask()
  const update = useUpdateTask()
  const complete = useCompleteTask()
  const remove = useDeleteTask()
  const [openTask, setOpenTask] = useState<Task | null>(null)

  // Ya viene ordenada por rank del API y la cache mantiene ese orden: volver a
  // ordenar acá desharía el movimiento optimista de un drag.
  const tasks = useMemo(() => query.data ?? [], [query.data])

  const columns = useMemo<KanbanColumnSpec<Task>[]>(
    () =>
      [...stages]
        .sort((a, b) => a.position - b.position)
        .map((stage) => ({
          id: stage.id,
          // El nombre de la columna lo escribe cada empresa: es texto de
          // usuario y viaja tal cual. Lo que decide color y reglas es la
          // categoría, que sí es nuestra.
          title: stage.name,
          category: stage.category,
          items: tasks.filter((task) => task.stage.id === stage.id),
        })),
    [stages, tasks],
  )

  // El sheet lee de la cache, no de su propio estado: un cambio optimista se ve
  // en el panel y en la tarjeta al mismo tiempo.
  const selected = openTask ? (tasks.find((task) => task.id === openTask.id) ?? null) : null

  if (query.isPending) return <SkeletonRows rows={6} />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  if (tasks.length === 0) {
    return (
      <EmptyState title={t('tasks.empty.title')} description={t('tasks.empty.boardDescription')} />
    )
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <KanbanBoard
        columns={columns}
        ariaLabel={t('tasks.board.ariaLabel')}
        getLabel={(task) => task.title}
        onMove={({ itemId, beforeId, afterId, toColumnId, changedColumn }) => {
          const stage = changedColumn
            ? stages.find((candidate) => candidate.id === toColumnId)
            : undefined

          move.mutate({ id: itemId, beforeId, afterId, stage })
        }}
        renderCard={(task, state) => (
          <TaskCard
            task={task}
            showProject={false}
            handle={state.handle}
            onOpen={setOpenTask}
            onToggleDone={(current, done) => complete.mutate({ id: current.id, done })}
          />
        )}
      />

      <TaskDetailSheet
        task={selected}
        stages={stages}
        onClose={() => setOpenTask(null)}
        onPatch={(task, patch, optimistic) => update.mutate({ id: task.id, patch, optimistic })}
        onMoveToStage={(task, stage) =>
          move.mutate({ id: task.id, beforeId: null, afterId: null, stage })
        }
        onToggleDone={(current, done) => complete.mutate({ id: current.id, done })}
        onDelete={(task) => remove.mutate(task.id)}
      />
    </div>
  )
}
